import { useHistory, useLocation } from 'react-router-dom'
import { errorMessage } from 'src/components/affiliate/labels'
import {
    Banner,
    ErrorText,
    Grid,
    InlineSpinner,
    LinkButton,
    Page,
    PageSubtitle,
    PageTitle,
    Tabs,
    Tile,
} from 'src/components/affiliate/ui'
import AffiliatesTab from 'src/components/admin-affiliates/AffiliatesTab'
import ApplicationsTab from 'src/components/admin-affiliates/ApplicationsTab'
import InvitesTab from 'src/components/admin-affiliates/InvitesTab'
import PayoutsTab from 'src/components/admin-affiliates/PayoutsTab'
import ProgramTab from 'src/components/admin-affiliates/ProgramTab'
import ReviewQueueTab from 'src/components/admin-affiliates/ReviewQueueTab'
import { useAdminOverview } from 'src/hooks/useAdminAffiliates'
import { formatCents } from 'src/utils/affiliateFormat'

const TAB_VALUES = ['affiliates', 'applications', 'review', 'payouts', 'invites', 'program'] as const
type Tab = typeof TAB_VALUES[number]

const isTab = (value: string | null): value is Tab => !!value && (TAB_VALUES as readonly string[]).indexOf(value) !== -1

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`

/** What needs an admin's action first, then the program's figures. */
const Overview = ({ onOpen }: { onOpen: (tab: Tab) => void }) => {
    const { data, isLoading, error } = useAdminOverview()
    if (isLoading) return <InlineSpinner />
    if (error) return <ErrorText>{errorMessage(error)}</ErrorText>
    if (!data) return null

    const todo = [
        data.pendingApplications > 0 && {
            tab: 'applications' as Tab,
            text: `${plural(data.pendingApplications, 'application', 'applications')} to review`,
        },
        data.heldCommissions > 0 && {
            tab: 'review' as Tab,
            text: `${plural(data.heldCommissions, 'commission', 'commissions')} held for review`,
        },
    ].filter(Boolean) as { tab: Tab; text: string }[]

    return (
        <>
            <Banner $tone={todo.length ? 'warn' : 'success'}>
                {todo.length ? (
                    todo.map((item) => (
                        <LinkButton
                            key={item.tab}
                            onClick={() => onOpen(item.tab)}
                            style={{ color: 'white', textDecoration: 'underline', marginRight: 16 }}
                        >
                            {item.text}
                        </LinkButton>
                    ))
                ) : (
                    <p>Nothing needs your attention.</p>
                )}
            </Banner>
            <Grid $min={170}>
                <Tile label="Active affiliates" value={data.activeAffiliates} />
                <Tile label="Monthly revenue" value={formatCents(data.mrrFromAffiliatesCents)} />
                <Tile label="Owed, unlocking" value={formatCents(data.owedPendingCents)} />
                <Tile
                    label="Owed, ready"
                    value={formatCents(data.owedAvailableCents)}
                    negative={data.owedAvailableCents < 0}
                />
                <Tile label="Paid, last 30 days" value={formatCents(data.paidLast30dCents)} />
            </Grid>
        </>
    )
}

const AdminAffiliatesPage = () => {
    const history = useHistory()
    const location = useLocation()
    const { data: overview } = useAdminOverview()

    const param = new URLSearchParams(location.search).get('tab')
    const tab: Tab = isTab(param) ? param : 'affiliates'

    const setTab = (next: Tab) => {
        const params = new URLSearchParams(location.search)
        params.set('tab', next)
        history.replace({ pathname: location.pathname, search: `?${params.toString()}` })
    }

    const count = (n: number | undefined) => (n ? ` (${n})` : '')

    return (
        <Page>
            <div>
                <PageTitle>Affiliates</PageTitle>
                <PageSubtitle>Manage affiliates, applications, held commissions, payouts and program settings.</PageSubtitle>
            </div>
            <Overview onOpen={setTab} />
            <Tabs<Tab>
                tabs={[
                    { value: 'affiliates', label: 'Affiliates' },
                    { value: 'applications', label: `Applications${count(overview?.pendingApplications)}` },
                    { value: 'review', label: `Review queue${count(overview?.heldCommissions)}` },
                    { value: 'payouts', label: 'Payouts' },
                    { value: 'invites', label: 'Invites' },
                    { value: 'program', label: 'Program' },
                ]}
                value={tab}
                onChange={setTab}
            />
            {tab === 'affiliates' && <AffiliatesTab />}
            {tab === 'applications' && <ApplicationsTab />}
            {tab === 'review' && <ReviewQueueTab />}
            {tab === 'payouts' && <PayoutsTab />}
            {tab === 'invites' && <InvitesTab />}
            {tab === 'program' && <ProgramTab />}
        </Page>
    )
}

export default AdminAffiliatesPage
